import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';

export type FormErrorSummaryItem = {
  id: string;
  label: string;
  message: ReactNode;
  /** 실제로 초점을 받을 입력 요소의 id입니다. */
  fieldId?: string;
  /** 접힌 항목을 펼친 뒤 초점을 옮기는 등 필드 이동을 직접 처리할 때 사용합니다. */
  onActivate?: () => void;
};

export type FormErrorSummaryProps = {
  items: readonly FormErrorSummaryItem[];
  title?: string;
  /** 제출·다음 단계 검증이 실패할 때 증가시킵니다. 입력 중에는 변경하지 않습니다. */
  focusRequest?: number;
};

/** 검증은 호출자가 소유하고, 오류 목록과 입력으로 이동하는 동작을 제공합니다. */
export function FormErrorSummary({ items, title = '입력 내용을 확인해 주세요.', focusRequest = 0 }: FormErrorSummaryProps) {
  const id = useId();
  const summaryRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (focusRequest < 1) return;
    const frame = window.requestAnimationFrame(() => summaryRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [focusRequest]);

  if (items.length === 0) return null;

  const activate = (item: FormErrorSummaryItem) => {
    if (item.onActivate) {
      item.onActivate();
    } else if (item.fieldId) {
      const target = document.getElementById(item.fieldId);
      target?.focus();
      target?.scrollIntoView({ block: 'nearest' });
    }
  };

  return <Box
    component="section"
    ref={summaryRef}
    tabIndex={-1}
    aria-labelledby={`${id}-title`}
    aria-describedby={`${id}-errors`}
    sx={{
      p: `${designTokens.feedback.notice.inset}px`,
      border: '1px solid',
      borderColor: designTokens.color.danger,
      borderRadius: designTokens.feedback.notice.radius,
      bgcolor: designTokens.color.dangerSoft,
      color: designTokens.color.primaryText,
      '&:focus': createFocusRingStyles({ inset: true }),
      '@media (forced-colors: active)': { borderColor: 'CanvasText' },
    }}
  >
    <Typography id={`${id}-title`} component="h3" variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
    <Box id={`${id}-errors`} component="ul" sx={{ m: 0, mt: 1, pl: 2.5, display: 'grid', gap: 0.5 }}>
      {items.map((item) => <Box component="li" key={item.id} sx={{ ...designTokens.typography.ui, overflowWrap: 'anywhere' }}>
        {item.fieldId || item.onActivate ? <Link
          component="button"
          type="button"
          onClick={() => activate(item)}
          sx={{
            ...designTokens.typography.ui,
            color: designTokens.color.dangerText,
            textAlign: 'left',
            overflowWrap: 'anywhere',
            borderRadius: designTokens.radius.compact,
            '&:focus-visible': createFocusRingStyles({ inset: true }),
          }}
        >{item.label}: {item.message}</Link> : <>{item.label}: {item.message}</>}
      </Box>)}
    </Box>
  </Box>;
}
